<template>
  <v-dialog :value="value" max-width="600px" @input="$emit('input', $event)">
    <v-card class="pa-2">
      <v-card-title class="d-flex align-center justify-space-between">
        <span class="text-subtitle-1 font-weight-bold">{{ isEdit ? 'Редактирование эксперта' : 'Новый эксперт' }}</span>
        <v-btn icon small @click="$emit('input', false)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <template v-if="isEdit">
          <div class="d-flex align-center mb-4">
            <v-avatar size="56" color="#483CB9" class="mr-3">
              <img v-if="editingExpert.photo_url" :src="editingExpert.photo_url" :alt="editingExpertName">
              <span v-else class="white--text text-subtitle-2">{{ initials(editingExpertName) }}</span>
            </v-avatar>
            <v-btn icon small class="mr-1" @click="triggerPhotoInput">
              <v-icon small>mdi-camera-outline</v-icon>
            </v-btn>
            <v-btn v-if="editingExpert.photo_url" icon small @click="$emit('delete-photo', editingExpert.id)">
              <v-icon small>mdi-image-remove</v-icon>
            </v-btn>
            <input ref="photoInput" type="file" accept="image/*" class="d-none" @change="onPhotoSelected">
          </div>

          <div class="text-caption grey--text text--darken-1 mb-1">Роль пользователя</div>
          <!-- Роль задаётся один раз: у экспертов, заведённых до появления поля, её можно проставить. -->
          <v-select
            v-if="!editingExpert.role"
            v-model="form.role"
            :items="roleOptions"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details
            placeholder="Не указана"
            class="mb-4"
          />
          <div v-else class="text-body-2 mb-4">{{ editingExpertRoleLabel }}</div>
        </template>

        <template v-if="!isEdit">
          <div class="text-caption grey--text text--darken-1 mb-1">Роль пользователя</div>
          <!--
            Общей точки получения всех юзеров платформы нет — эксперт может быть привязан
            только к менеджеру, партнёру или корп. клиенту, каждый со своим эндпоинтом поиска.
          -->
          <v-select
            v-model="form.role"
            :items="roleOptions"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details
            class="mb-4"
            @change="onRoleChange"
          />

          <div class="text-caption grey--text text--darken-1 mb-1">Пользователь платформы</div>
          <v-autocomplete
            v-model="form.user"
            :items="userItems"
            item-value="user"
            :item-text="userFullName"
            :loading="userLoading"
            outlined
            dense
            hide-details
            clearable
            no-filter
            no-data-text="Не найдено"
            placeholder="Начните вводить имя..."
            class="mb-4"
            @input="onUserInput"
            @update:search-input="onUserSearch"
          />
        </template>

        <div class="text-caption grey--text text--darken-1 mb-1">Короткое описание</div>
        <v-text-field
          v-model="form.short_description"
          outlined
          dense
          hide-details
          maxlength="500"
          class="mb-4"
        />

        <div class="text-caption grey--text text--darken-1 mb-1">Полное описание</div>
        <v-textarea
          v-model="form.description"
          outlined
          dense
          hide-details
          rows="3"
          class="mb-4"
        />

        <div class="text-caption grey--text text--darken-1 mb-1">Постоянная ссылка на конференцию</div>
        <!-- Подставляется бэком во встречу, если при подтверждении ссылка не указана. -->
        <v-text-field
          v-model="form.permanent_conference_url"
          outlined
          dense
          hide-details
          placeholder="https://..."
          class="mb-4"
        />

        <v-switch
          v-if="isEdit"
          v-model="form.is_active"
          label="Активен (виден в публичном расписании)"
          hide-details
          class="mt-0 mb-4"
        />

        <div class="d-flex justify-space-between align-center mb-2">
          <div class="text-caption grey--text text--darken-1">Услуги эксперта</div>
          <v-btn small text color="#483CB9" class="text-capitalize" :disabled="!hasAvailableTypeToAdd" @click="addServiceRow">
            <v-icon small left>mdi-plus</v-icon>
            Добавить
          </v-btn>
        </div>

        <div v-if="!serviceTypes.length" class="text-caption grey--text text-center py-2">
          Сначала добавьте хотя бы один тип услуги в справочнике
        </div>
        <div v-else-if="!form.services.length" class="text-caption grey--text text-center py-2">
          Услуги не назначены
        </div>
        <div v-else-if="!hasAvailableTypeToAdd" class="text-caption grey--text mb-2">
          Все доступные типы услуг уже назначены этому эксперту
        </div>
        <div
          v-for="(row, index) in form.services"
          :key="row.key"
          class="d-flex align-center mb-2 service-row"
          :class="{ 'grey--text': row.id && !row.is_active }"
        >
          <v-select
            v-model="row.service_type"
            :items="availableTypesForRow(row)"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details
            placeholder="Тип услуги"
            class="mr-2 service-row__type"
          />
          <v-text-field
            v-model.number="row.max_duration_minutes"
            type="number"
            step="15"
            min="15"
            outlined
            dense
            hide-details
            :error="!isRowDurationValid(row)"
            style="max-width: 96px;"
            class="mr-2"
          />
          <v-switch
            v-if="row.id"
            v-model="row.is_active"
            dense
            hide-details
            inset
            class="calendar-switch--small ma-0 pa-0 mr-1"
          />
          <v-btn v-else icon x-small @click="removeServiceRow(index)">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-btn block color="#483CB9" :dark="canSave" :loading="loading" :disabled="!canSave" @click="save">
          {{ isEdit ? 'Сохранить изменения' : 'Добавить эксперта' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

// Внутри диалога корп. клиент называется 'client' (как во всём приложении),
// а поле role эксперта на бэке ждёт 'corp_client'.
const EXPERT_ROLE_API_VALUES = {
  manager: 'manager',
  partner: 'partner',
  client: 'corp_client',
};

export default {
  name: 'ExpertFormDialog',
  props: {
    value: { type: Boolean, default: false },
    editingExpert: { type: Object, default: null },
    services: { type: Array, default: () => [] },
    serviceTypes: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      form: this.buildForm(),
      savedUser: null,
      skipUserSearch: false,
      userSearchTimer: null,
      rowKeySeq: 0,
    };
  },
  computed: {
    ...mapGetters('users/managers', { managerOptions: 'bookingSearchResults', managersLoading: 'bookingSearchLoading' }),
    ...mapGetters('users/partners', { partnerOptions: 'bookingSearchResults', partnersLoading: 'bookingSearchLoading' }),
    ...mapGetters('users/clients', { clientOptions: 'bookingSearchResults', clientsLoading: 'bookingSearchLoading' }),
    isEdit() {
      return Boolean(this.editingExpert);
    },
    roleOptions() {
      return [
        { value: 'manager', text: 'Менеджер' },
        { value: 'partner', text: 'Партнёр' },
        { value: 'client', text: 'Корп. клиент' },
      ];
    },
    userOptions() {
      if (this.form.role === 'partner') return this.partnerOptions;
      if (this.form.role === 'client') return this.clientOptions;
      return this.managerOptions;
    },
    userLoading() {
      if (this.form.role === 'partner') return this.partnersLoading;
      if (this.form.role === 'client') return this.clientsLoading;
      return this.managersLoading;
    },
    userItems() {
      const items = [...(this.userOptions || [])];
      if (this.savedUser && !items.find((item) => item.user === this.savedUser.user)) {
        items.unshift(this.savedUser);
      }
      return items;
    },
    serviceTypeOptions() {
      return this.serviceTypes.filter((item) => item.is_active !== false);
    },
    usedServiceTypeIds() {
      return this.form.services.map((row) => row.service_type);
    },
    hasAvailableTypeToAdd() {
      return this.serviceTypeOptions.some((type) => !this.usedServiceTypeIds.includes(type.id));
    },
    editingExpertRoleLabel() {
      const option = this.roleOptions.find((item) => EXPERT_ROLE_API_VALUES[item.value] === this.editingExpert.role);
      return option ? option.text : this.editingExpert.role;
    },
    editingExpertName() {
      if (!this.editingExpert) return '';
      return `${this.editingExpert.first_name || ''} ${this.editingExpert.last_name || ''}`.trim();
    },
    canSave() {
      if (!this.form.services.every((row) => this.isRowValid(row))) return false;
      if (this.isEdit) return true;
      return Boolean(this.form.user);
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.form = this.buildForm();
        this.savedUser = null;
      }
    },
  },
  methods: {
    ...mapActions('users/managers', { searchManagers: 'searchManagersForBooking' }),
    ...mapActions('users/partners', { searchPartners: 'searchPartnersForBooking' }),
    ...mapActions('users/clients', { searchClients: 'searchClientsForBooking' }),
    buildForm() {
      const services = this.editingExpert
        ? this.services.map((item) => ({
          key: `saved-${item.id}`,
          id: item.id,
          service_type: item.service_type,
          max_duration_minutes: item.max_duration_minutes,
          is_active: item.is_active !== false,
        }))
        : [];
      if (this.editingExpert) {
        return {
          role: null,
          user: this.editingExpert.user,
          short_description: this.editingExpert.short_description || '',
          description: this.editingExpert.description || '',
          permanent_conference_url: this.editingExpert.permanent_conference_url || '',
          is_active: this.editingExpert.is_active,
          services,
        };
      }
      return {
        role: 'manager',
        user: null,
        short_description: '',
        description: '',
        permanent_conference_url: '',
        is_active: true,
        services,
      };
    },
    isRowDurationValid(row) {
      const value = row.max_duration_minutes;
      return Number.isInteger(value) && value > 0 && value % 15 === 0;
    },
    isRowValid(row) {
      return Boolean(row.service_type) && this.isRowDurationValid(row);
    },
    // Не даёт выбрать тип, уже занятый другой строкой; свой текущий тип показывает всегда,
    // даже если он деактивирован в справочнике.
    availableTypesForRow(row) {
      const usedByOthers = this.form.services
        .filter((item) => item !== row)
        .map((item) => item.service_type);
      const options = this.serviceTypeOptions.filter((type) => !usedByOthers.includes(type.id));
      if (row.service_type && !options.some((type) => type.id === row.service_type)) {
        const ownType = this.serviceTypes.find((type) => type.id === row.service_type);
        if (ownType) options.push(ownType);
      }
      return options;
    },
    addServiceRow() {
      const nextType = this.serviceTypeOptions.find((type) => !this.usedServiceTypeIds.includes(type.id));
      if (!nextType) return;
      this.rowKeySeq += 1;
      this.form.services.push({
        key: `new-${this.rowKeySeq}`,
        id: null,
        service_type: nextType.id,
        max_duration_minutes: 60,
        is_active: true,
      });
    },
    removeServiceRow(index) {
      this.form.services.splice(index, 1);
    },
    initials(name) {
      return name.split(' ')
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    },
    triggerPhotoInput() {
      if (this.$refs.photoInput) this.$refs.photoInput.click();
    },
    onPhotoSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.$emit('upload-photo', { id: this.editingExpert.id, file });
      // сбрасываем value, чтобы повторный выбор того же файла тоже сработал
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
    },
    userFullName(user) {
      return `${user.last_name || ''} ${user.first_name || ''}`.trim();
    },
    onRoleChange() {
      this.form.user = null;
      this.savedUser = null;
    },
    onUserInput(val) {
      if (val) {
        this.skipUserSearch = true;
        this.savedUser = this.userOptions.find((item) => item.user === val) || null;
      } else {
        this.savedUser = null;
      }
    },
    onUserSearch(val) {
      if (val === null || val === undefined) return;
      if (this.skipUserSearch) {
        this.skipUserSearch = false;
        return;
      }
      clearTimeout(this.userSearchTimer);
      this.userSearchTimer = setTimeout(() => {
        if (this.form.role === 'partner') {
          this.searchPartners(val);
        } else if (this.form.role === 'client') {
          this.searchClients(val);
        } else {
          this.searchManagers(val);
        }
      }, 400);
    },
    save() {
      if (!this.canSave) return;
      const services = this.form.services.map((row) => ({
        id: row.id,
        service_type: row.service_type,
        max_duration_minutes: row.max_duration_minutes,
        is_active: row.is_active,
      }));
      if (this.isEdit) {
        this.$emit('save', {
          expert: {
            id: this.editingExpert.id,
            role: this.form.role ? EXPERT_ROLE_API_VALUES[this.form.role] : undefined,
            short_description: this.form.short_description,
            description: this.form.description,
            permanent_conference_url: this.form.permanent_conference_url.trim(),
            is_active: this.form.is_active,
          },
          services,
        });
      } else {
        this.$emit('save', {
          expert: {
            user: this.form.user,
            role: EXPERT_ROLE_API_VALUES[this.form.role],
            short_description: this.form.short_description,
            description: this.form.description,
            permanent_conference_url: this.form.permanent_conference_url.trim(),
          },
          services,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.service-row {
  .calendar-switch--small {
    transform: scale(0.75);
    transform-origin: center;
  }

  // Длинное название типа услуги не должно распирать строку и выталкивать
  // поле длительности/переключатель — селект занимает остаток ширины,
  // а сам текст выбранного значения обрезается многоточием.
  &__type {
    flex: 1 1 auto;
    min-width: 0;

    ::v-deep .v-select__selections {
      flex-wrap: nowrap;
      min-width: 0;
    }

    ::v-deep .v-select__selection--comma {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
